import { METER_TYPE, FIELD_NAME } from '../constants/MeterTypeFieldMap';
import { RECONCILIATION_WARNING_COMPARING_VALUE } from '../constants/CommonConstants';
import { getFieldName, getRevenueInUSD } from './collection-methods';
import {
  calculateDifferenceBetweenTwoFields,
  checkNumberIsNaNAndInfinity,
  getSum,
  isValueValid,
} from './common-methods';

// Methods for reconciliation warning
/**
 * Get current reading and prior reading field names and values
 *
 * @param {String} meterName
 * @param {String} sublocationId
 * @param {String} assetId
 * @param {String} meterId
 * @param {String} assetMeterId
 * @param {Object} values
 * @returns {Object}
 */
export const getReadingFieldValues = (meterName, sublocationId, assetId, meterId, assetMeterId, values) => {
  const currentReadingFieldName = getFieldName(
    meterName,
    FIELD_NAME.CURRENT_READING,
    sublocationId,
    assetId,
    meterId,
    assetMeterId,
  );
  const priorReadingFieldName = getFieldName(
    meterName,
    FIELD_NAME.PRIOR_READING,
    sublocationId,
    assetId,
    meterId,
    assetMeterId,
  );
  const priorReading = values ? values[priorReadingFieldName] : 0;
  const currentReading = values ? values[currentReadingFieldName] : 0;
  return { priorReading, currentReading, currentReadingFieldName, priorReadingFieldName };
};

/**
 * Get difference of coinsCollected - estimatedCoinRevenue of COIN and coinsCollected fieldName
 *
 * @param {String} sublocationId
 * @param {String} assetId
 * @param {String} meterId
 * @param {String} assetMeterId
 * @param {Object} values
 * @param {number} exchangeRate
 * @returns {Object}
 */
export const getCoinMeterDifference = (sublocationId, assetId, meterId, assetMeterId, values, exchangeRate) => {
  const coinCollectedFieldName = getFieldName(
    METER_TYPE.COIN,
    FIELD_NAME.COINS_COLLECTED,
    sublocationId,
    assetId,
    meterId,
    assetMeterId,
  );
  const estimatedCoinRevenueFieldName = getFieldName(
    METER_TYPE.COIN,
    FIELD_NAME.ESTIMATED_COIN_REVENUE,
    sublocationId,
    assetId,
    meterId,
    assetMeterId,
  );
  const coinsCollected = getRevenueInUSD(values[coinCollectedFieldName] ?? 0, exchangeRate);
  const estimatedCoinRevenue = values[estimatedCoinRevenueFieldName] ?? 0;
  const difference = calculateDifferenceBetweenTwoFields(estimatedCoinRevenue, coinsCollected);
  return { difference, coinCollectedFieldName };
};

/**
 * Get difference of creditRevenue - estimated credit revenue of Credit
 *
 * @param {String} sublocationId
 * @param {String} assetId
 * @param {String} meterId
 * @param {String} assetMeterId
 * @param {Object} values
 * @returns
 */
export const getCreditMeterDifference = (sublocationId, assetId, meterId, assetMeterId, values) => {
  const creditCardRevenueFieldName = getFieldName(
    METER_TYPE.CREDIT,
    FIELD_NAME.SEEDLIVE_REVENUE,
    sublocationId,
    assetId,
    meterId,
    assetMeterId,
  );
  const estimatedCreditCardRevenueFieldName = getFieldName(
    METER_TYPE.CREDIT,
    FIELD_NAME.EXPECTED_REVENUE,
    sublocationId,
    assetId,
    meterId,
    assetMeterId,
  );
  const creditCardRevenue = values[creditCardRevenueFieldName] ?? 0;
  const estimatedCreditCardRevenue = values[estimatedCreditCardRevenueFieldName] ?? 0;

  const difference = calculateDifferenceBetweenTwoFields(creditCardRevenue, estimatedCreditCardRevenue);
  return { difference, estimatedCreditCardRevenueFieldName };
};

/**
 * Get difference of creditRevenue - estimated credit revenue of Credit
 *
 * @param {*} sublocationId
 * @param {*} assetId
 * @param {*} meterId
 * @param {*} assetMeterId
 * @param {*} values
 * @param {*} exchangeRate
 * @returns
 */
export const getCashMeterDifference = (sublocationId, assetId, meterId, assetMeterId, values, exchangeRate) => {
  const billsCollectedFieldName = getFieldName(
    METER_TYPE.CASH,
    FIELD_NAME.BILLS_COLLECTED,
    sublocationId,
    assetId,
    meterId,
    assetMeterId,
  );
  const coinsCollectedFieldName = getFieldName(
    METER_TYPE.CASH,
    FIELD_NAME.COINS_COLLECTED,
    sublocationId,
    assetId,
    meterId,
    assetMeterId,
  );
  const adjustFieldName = getFieldName(
    METER_TYPE.CASH,
    FIELD_NAME.METER_ADJUST,
    sublocationId,
    assetId,
    meterId,
    assetMeterId,
  );
  const expectedCashRevenueFieldName = getFieldName(
    METER_TYPE.CASH,
    FIELD_NAME.EXPECTED_REVENUE,
    sublocationId,
    assetId,
    meterId,
    assetMeterId,
  );
  const coinsCollected = values ? values[coinsCollectedFieldName] : 0;
  const billsCollected = values ? values[billsCollectedFieldName] : 0;
  const adjust = values ? values[adjustFieldName] : 0;
  const expectedCashRevenue = values ? values[expectedCashRevenueFieldName] : 0;
  const sumOfCollected = getSum(
    checkNumberIsNaNAndInfinity(coinsCollected),
    checkNumberIsNaNAndInfinity(billsCollected),
  );
  const actualCashRevenue = getSum(sumOfCollected, checkNumberIsNaNAndInfinity(adjust));
  const sum = getRevenueInUSD(actualCashRevenue, exchangeRate);
  const difference = calculateDifferenceBetweenTwoFields(sum, expectedCashRevenue);

  return { difference, billsCollectedFieldName, coinsCollectedFieldName, adjustFieldName };
};

export const getTokenChangerDifference = (sublocationId, assetId, meterId, assetMeterId, values, exchangeRate) => {
  const tokenChangerFieldName = getFieldName(
    METER_TYPE.TOKEN_CHANGER,
    FIELD_NAME.REVENUE_COLLECTED_FROM_TOKEN_CHANGER,
    sublocationId,
    assetId,
    meterId,
    assetMeterId,
  );
  const seedLiveFieldName = getFieldName(
    METER_TYPE.TOKEN_CHANGER,
    FIELD_NAME.SEEDLIVE_REVENUE,
    sublocationId,
    assetId,
    meterId,
    assetMeterId,
  );
  const revenueCollected = getRevenueInUSD(values[tokenChangerFieldName], exchangeRate) ?? 0;
  const seedLiveRevenue = values[seedLiveFieldName] ?? 0;
  const difference = calculateDifferenceBetweenTwoFields(seedLiveRevenue - revenueCollected);
  return { difference, tokenChangerFieldName, seedLiveFieldName };
};

export const hasSeedliveRevenueVarianceWarning = (value) =>
  !isValueValid(value) || (String(value).trim() !== '' && Number(value) === 0);

export const getAssetHasReconciliationVarianceWarning = ({
  asset,
  values,
  exchangeRate,
  sublocationId: fallbackSublocationId,
}) => {
  if (!asset || !values || !Array.isArray(asset?.assetMeters) || asset.assetMeters.length === 0) {
    return false;
  }

  const sublocationId = asset?.subLocationId ?? fallbackSublocationId;

  return asset.assetMeters.some((assetMeter) => {
    const meterName = assetMeter?.meter?.name;
    const meterId = assetMeter?.meterId ?? assetMeter?.meter?.id;
    const assetMeterId = assetMeter?.id;

    if (!meterName || meterId == null || assetMeterId == null || sublocationId == null) {
      return false;
    }

    switch (meterName) {
      case METER_TYPE.CASH: {
        const { difference } = getCashMeterDifference(
          sublocationId,
          asset?.id,
          meterId,
          assetMeterId,
          values,
          exchangeRate,
        );
        return difference > RECONCILIATION_WARNING_COMPARING_VALUE;
      }
      case METER_TYPE.COIN: {
        const { difference } = getCoinMeterDifference(
          sublocationId,
          asset?.id,
          meterId,
          assetMeterId,
          values,
          exchangeRate,
        );
        return difference > RECONCILIATION_WARNING_COMPARING_VALUE;
      }
      case METER_TYPE.CREDIT: {
        const { difference } = getCreditMeterDifference(sublocationId, asset?.id, meterId, assetMeterId, values);
        if (difference > RECONCILIATION_WARNING_COMPARING_VALUE) {
          return true;
        }

        const seedliveRevenueFieldName = getFieldName(
          METER_TYPE.CREDIT,
          FIELD_NAME.SEEDLIVE_REVENUE,
          sublocationId,
          asset?.id,
          meterId,
          assetMeterId,
        );

        return hasSeedliveRevenueVarianceWarning(values?.[seedliveRevenueFieldName]);
      }
      case METER_TYPE.TOKEN_CHANGER: {
        const { difference } = getTokenChangerDifference(
          sublocationId,
          asset?.id,
          meterId,
          assetMeterId,
          values,
          exchangeRate,
        );
        return difference > RECONCILIATION_WARNING_COMPARING_VALUE;
      }
      default:
        return false;
    }
  });
};

export const getOnlyHasCreditAndTokenChangerEstCCRevenueWarning = (reconciliationWarningForModal) => {
  const warningObjects = {};
  const creditTokenCCRevenueWarning = {};

  Object.entries(reconciliationWarningForModal).forEach(([key, value]) => {
    const meterType = key?.split('_')[0];
    const fieldName = key?.split('_')[1];
    if (
      value &&
      [METER_TYPE.CREDIT, METER_TYPE.TOKEN_CHANGER].includes(meterType) &&
      [FIELD_NAME.EXPECTED_REVENUE, FIELD_NAME.REVENUE_COLLECTED_FROM_TOKEN_CHANGER].includes(fieldName)
    ) {
      creditTokenCCRevenueWarning[key] = value;
    }
    if (value) {
      warningObjects[key] = value;
    }
  });
  return Object.values(warningObjects).length === Object.values(creditTokenCCRevenueWarning).length;
};
